import * as React from 'react'
import type {
  AllAccessBannerButton,
  ClassesBannerButton,
  NotificationBannerButton,
  UndoCancelationBannerButton,
  UpsellBannerButton,
  VerificationBannerButton,
} from 'codegen/types'
import { useIsLoggedInQuery } from 'auth/hooks'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useStartFreeTrial } from 'payments/start-free-trial-button/hooks/use-start-free-trial'

interface Props {
  bannerAboveNav?: string
  bannerAboveNavPromo?: number
  button:
    | UpsellBannerButton
    | AllAccessBannerButton
    | UpsellBannerButton
    | NotificationBannerButton
    | ClassesBannerButton
    | VerificationBannerButton
    | UndoCancelationBannerButton
  onClick?: () => void
  loading?: boolean
}

const defaultConfig = {
  textColor: '#fff',
  bgColor: '#493675',
  hoverBgColor: '#2a1f3f',
}

const handleSavePaymentModalEntryPoint = (
  bannerAboveNav: string | undefined,
  bannerAboveNavPromo: number | undefined,
) => {
  let entryPoint = PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV
  if (window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)) {
    entryPoint = PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
  } else if (bannerAboveNav === 'promo_banner') {
    if (bannerAboveNavPromo === 927 || bannerAboveNavPromo === 4489) {
      entryPoint = PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_REGISTRATION
    } else {
      entryPoint = PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_PROMO
    }
  }
  savePaymentModalEntryPoint(entryPoint)
}

export function BannerButton({
  button,
  onClick,
  loading = false,
  bannerAboveNav = '',
  bannerAboveNavPromo,
}: Props) {
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { startFreeTrial } = useStartFreeTrial()
  const [hover, setHover] = React.useState(false)
  const {
    link,
    text: buttonText,
    textColor: buttonTextColor,
    backgroundColor: buttonBackgroundColor,
  } = button
  const buttonTextHoverColor = 'hoverTextColor' in button ? button.hoverTextColor : ''
  const buttonBackgroundHoverColor =
    'hoverBackgroundColor' in button ? button.hoverBackgroundColor : ''

  const buttonStyles = hover
    ? {
        color: buttonTextHoverColor || buttonTextColor || defaultConfig.textColor,
        backgroundColor:
          buttonBackgroundHoverColor || buttonBackgroundColor || defaultConfig.bgColor,
      }
    : {
        color: buttonTextColor || defaultConfig.textColor,
        backgroundColor: buttonBackgroundColor || defaultConfig.bgColor,
      }

  const buttonOnClick = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (loading) {
      e.preventDefault()
      return
    }

    handleSavePaymentModalEntryPoint(bannerAboveNav, bannerAboveNavPromo)

    if (onClick) {
      onClick()
    } else if (link) {
      const url = new URL(link)
      if ((url.pathname.startsWith('/p/') || url.pathname.startsWith('/b/')) && isLoggedIn) {
        e.preventDefault()
        await startFreeTrial()
      }
    }
  }

  return (
    <a
      className="inline-flex h-10 w-[220px] min-w-[64px] cursor-pointer items-center justify-center rounded-[4px] px-5 text-center text-[12px] font-bold uppercase leading-3 tracking-[0.9px] transition-colors md:text-left"
      href={link!}
      style={buttonStyles}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={buttonOnClick}
    >
      {loading ? <AnimatedSpinnerIcon /> : buttonText}
    </a>
  )
}
